import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Page } from "@/components/Layout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useCart } from "@/lib/useCart";
import { useCurrency } from "@/lib/currency";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { useAuth } from "@/lib/auth";
import { useAuthModal } from "@/components/AuthModal";
import { toast } from "sonner";
import { CheckCircle, Lock, CaretLeft, ArrowRight, DownloadSimple, Envelope, SignIn } from "@phosphor-icons/react";

const emptyForm = (user) => ({
  customer_name: user?.name || "",
  customer_email: user?.email || "",
  customer_phone: "",
  notes: "",
});

export default function CheckoutPage() {
  const { cart, total, clear } = useCart();
  const { format } = useCurrency();
  const { user } = useAuth();
  const { open: openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 details, 1 review, 2 done
  const [form, setForm] = useState(() => emptyForm(user));
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  // Digital-only: no shipping cost
  const grand = total;

  // Sync user fields once user loads
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
      }));
    }
  }, [user]);

  // Redirect to cart if empty (unless we're on success step)
  useEffect(() => {
    if (cart.length === 0 && step !== 2) navigate("/cart");
  }, [cart, step, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goReview = () => {
    if (!form.customer_name.trim() || !form.customer_email.trim() || form.customer_phone.trim().length < 7) {
      toast.error("Please fill name, email and phone"); return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.customer_email)) {
      toast.error("Enter a valid email"); return;
    }
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const placeOrder = async () => {
    if (!agreed) { toast.error("Please accept the Terms & Privacy Policy to continue"); return; }
    setBusy(true);
    try {
      const payload = {
        items: cart.map((c) => ({ id: c.id, name: c.name, qty: c.qty, price: c.price, image_url: c.image_url })),
        total: grand,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        notes: form.notes,
        address: "Digital delivery",
        address_line1: "Digital delivery",
        city: ",", state: ",", pincode: ",", country: "Digital",
      };
      const r = await api.post("/orders", payload);
      setOrder(r.data);
      await openRazorpayCheckout({
        orderType: "shop",
        orderId: r.data.id,
        amount: grand,
        name: form.customer_name,
        email: form.customer_email,
        description: `MindDhara Digital · ${cart.length} item${cart.length === 1 ? "" : "s"}`,
        onSuccess: () => {
          clear();
          setStep(2);
          window.scrollTo({ top: 0, behavior: "smooth" });
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order");
    } finally {
      setBusy(false);
    }
  };

  // ===== AUTH GATE, must be logged in to checkout =====
  if (!user && step !== 2) {
    return (
      <Page>
        <Seo title="Sign in to checkout" />
        <section className="md-section">
          <div className="md-container">
            <div className="checkout-narrow">
            <div className="md-card text-center" data-testid="checkout-auth-gate">
              <Lock size={42} weight="duotone" style={{ color: "var(--md-primary)" }} className="mx-auto mb-4" />
              <div className="md-label-eyebrow mb-2">Secure Checkout</div>
              <h1 className="text-3xl font-serif mb-3">Sign in to continue</h1>
              <p className="text-sm mb-6" style={{ color: "var(--md-text-soft)" }}>
                We need an account so your digital purchases are linked to your dashboard and downloads stay safe.
              </p>
              <button onClick={() => openAuthModal("login")} className="btn-primary w-full justify-center" data-testid="checkout-signin-btn">
                <SignIn size={14} weight="bold" /> Sign in
              </button>
              <button onClick={() => openAuthModal("register")} className="btn-outline w-full justify-center mt-3" data-testid="checkout-create-account-btn">
                Create an account
              </button>
              <button onClick={() => navigate("/cart")} className="text-sm mt-5" style={{ color: "var(--md-text-muted)", textDecoration: "underline" }} data-testid="checkout-back-cart">
                ← Back to cart
              </button>
            </div>
            </div>
          </div>
        </section>
      </Page>
    );
  }

  // ===== SUCCESS STATE =====
  if (step === 2) {
    return (
      <Page>
        <Seo title="Order Confirmed" />
        <section className="md-section">
          <div className="md-container">
            <div className="checkout-narrow text-center">
            <CheckCircle size={72} weight="duotone" style={{ color: "var(--md-primary)" }} className="mx-auto mb-5" />
            <h1 className="text-4xl md:text-5xl font-serif">Order placed</h1>
            <p className="text-base mt-3" style={{ color: "var(--md-text-soft)" }}>
              Download links have been emailed to <strong>{form.customer_email}</strong>. You can also access them anytime from{" "}
              <Link to="/dashboard" style={{ color: "var(--md-primary)", textDecoration: "underline" }}>your dashboard</Link>.
            </p>
            <div className="md-card text-left mt-7" data-testid="checkout-success-card">
              <div className="text-sm" style={{ color: "var(--md-text-muted)" }}>Order ID</div>
              <div className="font-mono text-base">{order?.id?.slice(0, 12)}</div>
              <div className="text-sm mt-3" style={{ color: "var(--md-text-muted)" }}>Total paid</div>
              <div className="text-xl font-serif">{format(grand)}</div>
            </div>
            <Link to="/shop" className="btn-primary mt-7" data-testid="checkout-success-shop">Continue shopping <ArrowRight size={14} weight="bold" /></Link>
            </div>
          </div>
        </section>
      </Page>
    );
  }

  return (
    <Page>
      <Seo title="Checkout" />
      <section className="md-section pt-8 md:pt-12">
        <div className="md-container">
          <button onClick={() => step === 0 ? navigate("/cart") : setStep(0)} className="pd-back" data-testid="checkout-back">
            <CaretLeft size={14} weight="bold" /> {step === 0 ? "Back to cart" : "Edit details"}
          </button>

          <div className="flex items-end justify-between flex-wrap gap-3 mt-5 mb-7">
            <div>
              <div className="md-label-eyebrow mb-2">Secure Checkout</div>
              <h1 className="text-3xl md:text-4xl font-serif">{step === 0 ? "Your details" : "Review & pay"}</h1>
            </div>
            <ol className="checkout-steps" data-testid="checkout-steps">
              <li className={step >= 0 ? "active" : ""}><span>1</span> Details</li>
              <li className={step >= 1 ? "active" : ""}><span>2</span> Review</li>
              <li className={step >= 2 ? "active" : ""}><span>3</span> Done</li>
            </ol>
          </div>

          <div className="cart-layout">
            <div>
              {step === 0 && (
                <div className="md-card" data-testid="checkout-details-form">
                  <div className="checkout-digital-banner" data-testid="checkout-digital-banner">
                    <DownloadSimple size={20} weight="duotone" />
                    <div>
                      <strong>Digital delivery</strong>
                      <p>Your books, decks and workbooks are delivered instantly via email. No shipping needed.</p>
                    </div>
                  </div>

                  <h3 className="font-serif text-lg mt-6 mb-4">Contact information</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Full name" required>
                      <input className="md-input" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="checkout-name" />
                    </Field>
                    <Field label="Delivery email" required>
                      <input type="email" className="md-input" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="checkout-email" />
                    </Field>
                    <Field label="Phone" required>
                      <input type="tel" required className="md-input" value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} data-testid="checkout-phone" />
                    </Field>
                    <Field label="Order notes (optional)" colSpan={2}>
                      <textarea className="md-input" rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="checkout-notes" />
                    </Field>
                  </div>

                  <p className="text-xs mt-5 flex items-center gap-1.5" style={{ color: "var(--md-text-muted)" }}>
                    <Envelope size={12} weight="bold" /> Download links will be sent to <strong style={{ color: "var(--md-text)" }}>{form.customer_email || "your email"}</strong>.
                  </p>

                  <div className="flex justify-end mt-6">
                    <button onClick={goReview} className="btn-primary" data-testid="checkout-continue">
                      Continue to review <ArrowRight size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="md-card" data-testid="checkout-review">
                  <h3 className="font-serif text-lg mb-4">Order items</h3>
                  <ul className="space-y-3">
                    {cart.map((c) => (
                      <li key={c.id} className="flex gap-3 items-center" data-testid={`checkout-item-${c.id}`}>
                        {c.image_url && <img src={c.image_url} alt={c.name} className="w-14 h-14 rounded-lg object-cover" />}
                        <div className="flex-1">
                          <div className="text-sm font-medium">{c.name}</div>
                          <div className="text-xs" style={{ color: "var(--md-text-muted)" }}>{format(c.price)} × {c.qty}</div>
                        </div>
                        <div className="font-medium">{format(c.price * c.qty)}</div>
                      </li>
                    ))}
                  </ul>

                  <h3 className="font-serif text-lg mt-7 mb-2">Delivery</h3>
                  <p className="text-sm" style={{ color: "var(--md-text-soft)" }}>
                    <strong>{form.customer_name}</strong><br />
                    Digital download via <strong style={{ color: "var(--md-text)" }}>{form.customer_email}</strong>
                    {form.customer_phone && <> · {form.customer_phone}</>}
                  </p>

                  <label className="checkout-terms" data-testid="checkout-terms">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      data-testid="checkout-terms-checkbox"
                    />
                    <span>
                      I have read and agree to the{" "}
                      <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" data-testid="checkout-terms-link">Terms &amp; Conditions</Link>
                      {" "}and{" "}
                      <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="checkout-privacy-link">Privacy Policy</Link>.
                      Digital products are non-refundable once downloaded.
                    </span>
                  </label>

                  <div className="flex items-center justify-between mt-6 pt-5 border-t" style={{ borderColor: "var(--md-border)" }}>
                    <div>
                      <div className="text-xs uppercase tracking-wider" style={{ color: "var(--md-text-muted)" }}>Total</div>
                      <div className="text-2xl font-serif">{format(grand)}</div>
                    </div>
                    <button onClick={placeOrder} disabled={busy || !agreed} className="btn-primary" data-testid="checkout-place-order">
                      <Lock size={14} weight="bold" /> {busy ? "Processing…" : `Pay ${format(grand)}`}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <aside className="cart-summary" data-testid="checkout-summary">
              <h3 className="font-serif text-xl mb-4">Summary</h3>
              <ul className="space-y-2 text-sm">
                {cart.map((c) => (
                  <li key={c.id} className="flex justify-between gap-3">
                    <span className="truncate"><strong>{c.qty}×</strong> {c.name}</span>
                    <span>{format(c.price * c.qty)}</span>
                  </li>
                ))}
              </ul>
              <div className="cart-summary-divider" />
              <div className="cart-summary-row"><span>Subtotal</span><strong>{format(total)}</strong></div>
              <div className="cart-summary-row">
                <span>Delivery</span>
                <strong><em style={{ color: "var(--md-primary)" }}>Free (digital)</em></strong>
              </div>
              <div className="cart-summary-divider" />
              <div className="cart-summary-row cart-summary-grand"><span>Total</span><strong>{format(grand)}</strong></div>
              <p className="text-xs mt-4 flex items-center gap-1.5" style={{ color: "var(--md-text-muted)" }}>
                <Lock size={12} weight="bold" /> Secure payment by Razorpay
              </p>
            </aside>
          </div>
        </div>
      </section>
    </Page>
  );
}

function Field({ label, required, children, colSpan }) {
  return (
    <div style={colSpan === 2 ? { gridColumn: "1 / -1" } : {}}>
      <label className="md-label-eyebrow block mb-1.5">{label}{required && <span style={{ color: "#B33A3A" }}> *</span>}</label>
      {children}
    </div>
  );
}
